/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable no-plusplus */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { array, func } from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { isPortraitScreen } from '../../../../helpers/Utils/screenOrientation';
import { selectDeviceType, selectIsNative } from '../../../../selectors/ContextSelector';
import PaginationScroll from '../PaginationScroll/PaginationScroll';
import PaginationShadow from '../PaginationShadow/PaginationShadow';
import * as styles from './SizeChartTable.css';

const cx = classNames.bind(styles);

export const textCase = (string) => string.charAt(0).toUpperCase() + string.slice(1);

// ovreflow only for x overflow
export const isOverFlow = (e) => e.offsetWidth < e.scrollWidth;

export const SizeChartTable = ({ sizeMeasurements = [], updateChartWidth }) => {
    const dataSizeChartTable = useRef();
    const dataSizeChartDiv = useRef();
    const sizeHeader = useRef();
    const [disableRight, setDisableRight] = useState(false);
    const [disableLeft, setDisableLeft] = useState(true);
    const [overflowActive, setOverflowActive] = useState(false);
    const [sizeColWidth, setSizeColWidth] = useState(0);
    const [headerWidth, setHeaderWidth] = useState();
    const [scrollPosition, setScrollPosition] = useState('scrollLeft'); // since scroll is on left initially
    const deviceType = useSelector((state) => selectDeviceType(state));
    const isNative = useSelector((state) => selectIsNative(state));
    /* istanbul ignore next */
    const onScroll = useCallback((event) => {
        const offset = 5;
        if (event.target.scrollLeft < offset) {
            setScrollPosition('scrollLeft');
        } else if (
            event.target.scrollLeft + event.target.offsetWidth >
            dataSizeChartTable.current.offsetWidth - offset
        ) {
            setScrollPosition('scrollRight');
        } else {
            setScrollPosition('scrollBetween');
        }
    }, []);
    /* istanbul ignore next */
    const handleScroll = (disableLeft, disableRight) => {
        if (disableLeft && dataSizeChartDiv && dataSizeChartDiv.current) {
            dataSizeChartDiv.current.scrollLeft = 0;
            setDisableLeft(disableLeft);
            setDisableRight(disableRight);
        }
        if (disableRight && dataSizeChartDiv && dataSizeChartDiv.current) {
            /*
                trick here is for animation scrollLeft we are scrolling maximum left which will automatic take max value accroding to sceen since this is higest number
            */
            dataSizeChartDiv.current.scrollLeft = 12000;
            setDisableLeft(disableLeft);
            setDisableRight(disableRight);
        }
    };
    /*
        checking here if table is scrollable or not . based on input seeting overflow active true
    */

    /* istanbul ignore next */
    const findRowColumnIndex = (event) => {
        const table = dataSizeChartTable.current;
        if (table && deviceType.isDesktop) {
            const rows = dataSizeChartTable.current.querySelectorAll('tr');
            const rowsArray = Array.from(rows);
            const rowIndex = rowsArray.findIndex((row) => row.contains(event.target));
            if (rowsArray && rowIndex) {
                const columns =
                    rowsArray[rowIndex] && Array.from(rowsArray[rowIndex].querySelectorAll('td'));
                const columnIndex =
                    columns && columns.findIndex((column) => column === event.target) + 1;
                if (rowIndex > -1 && columnIndex && columnIndex > -1) {
                    for (let i = 0; i < rowIndex; i++) {
                        table.rows[i].cells[columnIndex].className =
                            event.type === 'mouseover' ? cx('hovered') : null;
                    }
                    for (let i = 0; i < columnIndex; i++) {
                        table.rows[rowIndex].cells[i].className =
                            event.type === 'mouseover' ? cx('hovered') : null;
                    }
                }
            }
        }
    };

    useEffect(() => {
        if (dataSizeChartDiv && dataSizeChartDiv.current) {
            setOverflowActive(isOverFlow(dataSizeChartDiv.current));
            setHeaderWidth(dataSizeChartDiv.current.getBoundingClientRect().width / 2);
            setSizeColWidth(sizeHeader.current.getBoundingClientRect().width);
            updateChartWidth(dataSizeChartTable.current.clientWidth);
        }
    }, [sizeMeasurements]);

    useEffect(() => {
        if (dataSizeChartDiv && dataSizeChartDiv.current && isNative) {
            dataSizeChartDiv.current.style.maxHeight = `${window.innerHeight - 100}px`;
        }
    }, [isNative]);

    const tableHeader = (data = []) => (
        <thead>
            <tr>
                {deviceType.isMobile && isPortraitScreen() ? (
                    <th style={{ minWidth: headerWidth }} ref={sizeHeader}>
                        Size
                    </th>
                ) : (
                    <th ref={sizeHeader}>Size</th>
                )}
                {data.map((col) => {
                    return <th>{textCase(col.name)}</th>;
                })}
            </tr>
        </thead>
    );

    const tableRow = (data = {}) => (
        <tr>
            <th>{data.size}</th>
            {data.measurementParameters &&
                data.measurementParameters.map((col) => <td>{col.value}</td>)}
        </tr>
    );

    const measurementParameters =
        sizeMeasurements && sizeMeasurements[0] && sizeMeasurements[0].measurementParameters;
    const hasTableHeaders =
        measurementParameters &&
        measurementParameters[0] &&
        measurementParameters[0].name !== undefined;

    const hasOnlyTwoColumn = hasTableHeaders
        ? measurementParameters &&
          Array.isArray(measurementParameters) &&
          measurementParameters.length === 1
        : measurementParameters &&
          Array.isArray(measurementParameters) &&
          measurementParameters.length === 2;
    const chartTableClasses =
        hasOnlyTwoColumn && !overflowActive
            ? `${styles.sizeChartTable} ${dt(['w-full'])}`
            : styles.sizeChartTable;
    return sizeMeasurements.length ? (
        <>
            <div className={`${dt(['relative'])}`}>
                <PaginationShadow
                    headerWidth={
                        deviceType.isMobile && isPortraitScreen() ? headerWidth : sizeColWidth
                    }
                    scrollPosition={scrollPosition}
                    overflowActive={overflowActive}
                />
                <div
                    className={
                        overflowActive
                            ? `${styles.chartTable} ${dt([
                                  'overflow-x-scroll',
                                  'overflow-y-visible',
                              ])}`
                            : ''
                    }
                    ref={dataSizeChartDiv}
                    data-automation-id="pagination-outer-div"
                    onScroll={onScroll}
                >
                    <table
                        className={chartTableClasses}
                        onMouseOver={findRowColumnIndex}
                        onMouseOut={findRowColumnIndex}
                        ref={dataSizeChartTable}
                    >
                        {hasTableHeaders && tableHeader(measurementParameters)}
                        <tbody>
                            {sizeMeasurements && sizeMeasurements.map((row) => tableRow(row))}
                        </tbody>
                    </table>
                </div>
            </div>
            {deviceType?.isDesktop && overflowActive ? (
                <PaginationScroll
                    handleScroll={handleScroll}
                    disableLeft={disableLeft}
                    disableRight={disableRight}
                />
            ) : null}
        </>
    ) : null;
};

SizeChartTable.propTypes = {
    sizeMeasurements: array,
    updateChartWidth: func,
};

SizeChartTable.defaultProps = {
    sizeMeasurements: [],
    updateChartWidth: () => {},
};

export default SizeChartTable;
